'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  LayoutDashboard, ShoppingCart, Package, Users, Truck,
  CreditCard, BarChart2, ChevronLeft, ChevronRight,
  Store, LogOut, Key, ClipboardList, ChevronDown, ChevronUp
} from 'lucide-react';
import RetailerPinGate from './RetailerPinGate';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  label: string;
  labelFr: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavSection {
  key: string;
  label: string;
  labelFr: string;
  items: NavItem[];
}

const navSections: NavSection[] = [
  {
    key: 'main',
    label: 'المحل',
    labelFr: 'Mon Commerce',
    items: [
      { label: 'لوحة التحكم', labelFr: 'Tableau de Bord', href: '/retailer', icon: LayoutDashboard },
      { label: 'نقطة البيع (POS)', labelFr: 'Caisse POS', href: '/retailer/pos', icon: ShoppingCart },
      { label: 'المخزون', labelFr: 'Inventaire', href: '/retailer/inventory', icon: Package },
    ],
  },
  {
    key: 'orders',
    label: 'الطلبيات',
    labelFr: 'Commandes',
    items: [
      { label: 'سلة الطلب الذكية', labelFr: 'Panier Intelligent', href: '/retailer/smart-order', icon: ClipboardList },
      { label: 'تتبع الطلبيات', labelFr: 'Suivi Commandes', href: '/retailer/supplier-orders', icon: Truck },
      { label: 'كتالوج المورد', labelFr: 'Catalogue Fournisseur', href: '/retailer/catalog-access', icon: Key },
    ],
  },
  {
    key: 'finance',
    label: 'المالية',
    labelFr: 'Finance',
    items: [
      { label: 'كريدي الزبائن', labelFr: 'Crédit Clients', href: '/retailer/customer-credit', icon: Users },
      { label: 'كشف حساب الموردين', labelFr: 'Relevé Fournisseurs', href: '/retailer/supplier-credit', icon: CreditCard },
      { label: 'تقارير المبيعات', labelFr: 'Rapports Ventes', href: '/retailer/sales-reports', icon: BarChart2 },
    ],
  },
];

export default function RetailerLayout({ children }: { children: React.ReactNode }) {
  const [collapsed, setCollapsed] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    main: true, orders: true, finance: true,
  });
  const [pinAuthenticated, setPinAuthenticated] = useState(false);
  const [activeStaff, setActiveStaff] = useState<{ name: string; role: string } | null>(null);
  const pathname = usePathname();

  const isActive = (href: string) => {
    if (href === '/retailer') return pathname === '/retailer';
    return pathname.startsWith(href);
  };

  const toggleSection = (key: string) => {
    setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  if (!pinAuthenticated) {
    return (
      <RetailerPinGate
        onAuthenticated={(staff) => {
          setActiveStaff(staff);
          setPinAuthenticated(true);
        }}
      />
    );
  }

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Retailer Sidebar */}
      <aside
        className="flex-shrink-0 bg-card border-r border-border flex flex-col h-screen sticky top-0 z-30 transition-all duration-200"
        style={{ width: collapsed ? '56px' : '220px' }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-3 py-4 border-b border-border min-h-[56px]">
          <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center flex-shrink-0">
            <Store size={16} className="text-white" />
          </div>
          {!collapsed && (
            <div className="overflow-hidden flex-1">
              <span className="font-bold text-sm text-foreground block truncate">حانوت</span>
              <span className="text-2xs text-muted-foreground block">Épicerie</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-6 h-6 flex items-center justify-center rounded-lg hover:bg-muted transition-colors text-muted-foreground flex-shrink-0"
          >
            {collapsed ? <ChevronRight size={13} /> : <ChevronLeft size={13} />}
          </button>
        </div>

        {/* Staff badge */}
        {!collapsed && activeStaff && (
          <div className="px-3 pt-2 pb-1">
            <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
              <Key size={11} className="text-emerald-600" />
              <div className="flex-1 min-w-0">
                <p className="text-2xs font-semibold text-emerald-800 truncate">{activeStaff.name}</p>
                <p className="text-2xs text-emerald-600">{activeStaff.role}</p>
              </div>
            </div>
          </div>
        )}

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {navSections.map((section) => (
            <div key={section.key} className="mb-1">
              {!collapsed && (
                <button
                  onClick={() => toggleSection(section.key)}
                  className="w-full flex items-center justify-between px-2 py-1.5 mt-1"
                >
                  <p className="text-2xs font-bold uppercase tracking-wider text-muted-foreground">
                    {section.label}
                  </p>
                  {expandedSections[section.key]
                    ? <ChevronUp size={11} className="text-muted-foreground" />
                    : <ChevronDown size={11} className="text-muted-foreground" />
                  }
                </button>
              )}
              {(collapsed || expandedSections[section.key]) && section.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.href);
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    title={collapsed ? item.labelFr : undefined}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs font-medium transition-all relative group ${
                      active
                        ? 'bg-emerald-600 text-white' :'text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    <Icon size={15} className="flex-shrink-0" />
                    {!collapsed && <span className="truncate">{item.label}</span>}
                    {collapsed && (
                      <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                        {item.labelFr}
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>

        {/* Bottom */}
        <div className="border-t border-border p-2 space-y-0.5">
          <Link
            href="/"
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-muted-foreground hover:bg-muted transition-colors relative group"
            title={collapsed ? 'Retour Fournisseur' : undefined}
          >
            <LogOut size={15} className="flex-shrink-0" />
            {!collapsed && <span>← واجهة المورد</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Retour Fournisseur
              </div>
            )}
          </Link>
          <button
            onClick={() => { setActiveStaff(null); setPinAuthenticated(false); }}
            className="w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-xs text-danger hover:bg-danger/10 transition-colors relative group"
            title={collapsed ? 'Déconnexion' : undefined}
          >
            <Key size={15} className="flex-shrink-0" />
            {!collapsed && <span>تغيير الدور</span>}
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50">
                Changer rôle
              </div>
            )}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Topbar */}
        <header className="h-14 bg-card border-b border-border flex items-center px-4 gap-3 flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded bg-emerald-600 flex items-center justify-center">
              <Store size={13} className="text-white" />
            </div>
            <span className="font-semibold text-sm text-foreground">حانوت بقالة — Épicerie</span>
          </div>
          <div className="flex-1" />
          {activeStaff && (
            <span className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded-md">
              {activeStaff.name} · {activeStaff.role}
            </span>
          )}
        </header>
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-screen-2xl mx-auto px-4 lg:px-6 py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
